import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { keyframes } from "@mui/system";
import type { PaletteColor } from "../types/PaletteColor.ts";
import {
  PaletteColorHex,
  PaletteColorBg,
  PaletteColorDarkHex,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";

interface LoadingOverlayProps {
  message?: string;
  color?: PaletteColor;
}

const spinClockwise = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const spinCounterClockwise = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
`;

const jump = keyframes`
  0%,100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  message = "Loading...",
  color,
}) => {
  const theme = useTheme();
  const isDarkMode = useDarkMode();
  const loadingText = message;
  const [activeIndex, setActiveIndex] = useState(0);

  // Fallback colors if no color prop is provided
  const primaryColor = color
    ? isDarkMode
      ? PaletteColorDarkHex[color]
      : PaletteColorHex[color]
    : theme.palette.primary.main;
  const secondaryColor = color
    ? isDarkMode
      ? PaletteColorDarkHex[color]
      : PaletteColorHex[color]
    : theme.palette.primary.light;

  useEffect(() => {
    if (!loadingText) return;
    const interval = setInterval(
      () => setActiveIndex((i) => (i + 1) % loadingText.length),
      500
    );
    return () => clearInterval(interval);
  }, [loadingText]);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        bgcolor: isDarkMode ? "rgba(17,24,39,0.95)" : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(2px)",
        zIndex: 9999,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <SettingsIcon
          sx={{
            fontSize: 80,
            color: primaryColor,
            animation: `${spinClockwise} 2s linear infinite`,
          }}
        />
        <SettingsIcon
          sx={{
            fontSize: 60,
            color: secondaryColor,
            animation: `${spinCounterClockwise} 2.5s linear infinite`,
            ml: "-20px",
          }}
        />
      </Box>

      {loadingText && (
        <Typography
          variant="h6"
          sx={{
            mt: 3,
            letterSpacing: "0.1em",
            display: "flex",
            fontWeight: 600,
            userSelect: "none",
          }}
        >
          {loadingText.split("").map((char, idx) => (
            <Box
              component="span"
              key={idx}
              sx={{
                color: primaryColor,
                animation:
                  activeIndex === idx ? `${jump} 0.5s ease-in-out` : "none",
                px: "2px",
                minWidth: "0.5ch",
              }}
            >
              {char === " " ? "\u00A0" : char}
            </Box>
          ))}
        </Typography>
      )}
    </Box>
  );
};

export default LoadingOverlay;
